import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowTrendingUpIcon,
  ChartBarIcon,
  ArrowRightIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS, // Importar Chart principal para registrar componentes
  LineElement,      // Elemento de línea para gráficos de tipo Line
  PointElement,     // Elemento de punto para los marcadores en la línea
  LinearScale,      // Escala lineal para el eje Y
  Title,            // Plugin de título del gráfico
  CategoryScale,    // Escala de categorías para el eje X
  Tooltip,          // Plugin de tooltip al pasar el mouse
  Legend            // Plugin de leyenda del gráfico
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { dashboardApi, insightsApi, participantApi } from '../services/api';
import ReactMarkdown from 'react-markdown';

// Registrar los componentes de Chart.js necesarios usando Chart.register()
ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  Tooltip,
  Legend
);

function Progress() {
  const navigate = useNavigate();
  const [participant, setParticipant] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [maturityDistribution, setMaturityDistribution] = useState({});
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(6);

  // Estado para la IA (Tendencia)
  const [aiInsight, setAiInsight] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);

  // Estado para la Matriz DOFA
  const [dofaInsight, setDofaInsight] = useState(null);
  const [loadingDofa, setLoadingDofa] = useState(false);
  const [recentSessions, setRecentSessions] = useState([]);

  useEffect(() => {
    // Buscar la sesión activa del participante en el localStorage
    const activeParticipantStr = localStorage.getItem('activeParticipant');
    
    // Si la sesión es válida, cargar el progreso específico de este participante
    if (activeParticipantStr) {
      // Parsear la cadena JSON a un objeto JavaScript
      const activeParticipant = JSON.parse(activeParticipantStr);
      // Guardar el participante en el estado local de la página
      setParticipant(activeParticipant);
      // Solicitar al backend los datos históricos del participante usando su ID real
      loadData(activeParticipant.id);
    } else {
      // Redirigir a la pantalla de login si no hay sesión iniciada
      navigate('/login');
    }
  // loadData usa el rango seleccionado y se ejecuta al cambiarlo.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, navigate]);

  const loadData = async (participantId) => {
    try {
      setLoading(true);

      // Get progress data
      const progressResponse = await dashboardApi.getProgress(participantId, { months: timeRange });
      setProgressData(progressResponse.data);

      // Get maturity distribution
      const distributionResponse = await dashboardApi.getMaturityDistribution(participantId);
      setMaturityDistribution(distributionResponse.data);

      // Obtener las sesiones reales para tener el session_id
      const sessionsResponse = await participantApi.getTestSessions(participantId);
      const sessions = sessionsResponse.data || [];
      setRecentSessions(sessions);

      // Intentar cargar insight de IA si ya existe para progreso
      try {
        const aiResp = await insightsApi.getInsight(participantId, 'progress');
        if (aiResp.data) {
          setAiInsight(aiResp.data.insight_text);
        }
      } catch (e) {
        if (e.response && e.response.status !== 404) console.error('Error IA progreso:', e);
      }

      // Intentar cargar DOFA si ya existe para la última sesión
      if (sessions.length > 0) {
        try {
          const dofaResp = await insightsApi.getInsight(participantId, 'dofa_matrix', sessions[0].id);
          if (dofaResp.data && dofaResp.data.insight_text) {
            try {
              const parsed = JSON.parse(dofaResp.data.insight_text);
              setDofaInsight(parsed);
            } catch(e) {
              console.error("Error parseando DOFA existente", e);
            }
          }
        } catch (e) {
          if (e.response && e.response.status !== 404) console.error('Error IA DOFA:', e);
        }
      }

    } catch (error) {
      toast.error('Error al cargar los datos de progreso');
      console.error('Progress load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInsight = async () => {
    try {
      setLoadingAi(true);
      const response = await insightsApi.generateInsight(participant.id, 'progress');
      setAiInsight(response.data.insight_text);
      toast.success('¡Análisis de tendencia generado!');
    } catch (error) {
      console.error('Error generando IA progreso:', error);
      toast.error('No se pudo generar el análisis. Verifica que la API Key esté configurada.');
    } finally {
      setLoadingAi(false);
    }
  };

  const handleGenerateDofa = async () => {
    if (recentSessions.length === 0) {
      toast.error('Necesitas al menos una sesión de tests para generar tu DOFA.');
      return;
    }
    try {
      setLoadingDofa(true);
      const response = await insightsApi.generateInsight(participant.id, 'dofa_matrix', recentSessions[0].id);
      const parsed = JSON.parse(response.data.insight_text);
      setDofaInsight(parsed);
      toast.success('¡Matriz DOFA generada exitosamente!');
    } catch (error) {
      console.error('Error generando DOFA:', error);
      toast.error('Ocurrió un error al generar la Matriz DOFA. Intenta de nuevo.');
    } finally {
      setLoadingDofa(false);
    }
  };

  const chartData = {
    labels: progressData?.test_progress?.map(session =>
      new Date(session.session_date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })
    ) || [],
    datasets: [
      {
        label: 'MBTI',
        data: progressData?.test_progress?.map(session => session.mbti_score) || [],
        borderColor: 'rgb(99, 102, 241)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        tension: 0.1
      },
      {
        label: 'VIA',
        data: progressData?.test_progress?.map(session => session.via_score) || [],
        borderColor: 'rgb(147, 51, 234)',
        backgroundColor: 'rgba(147, 51, 234, 0.1)',
        tension: 0.1
      },
      {
        label: 'Eneagrama',
        data: progressData?.test_progress?.map(session => session.enneagram_score) || [],
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        tension: 0.1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Evolución de tus Resultados'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function (value) {
            return value + '%';
          }
        }
      }
    }
  };

  const getMaturityLevelColor = (score) => {
    if (score >= 76) return 'maturity-76-100';
    if (score >= 51) return 'maturity-51-75';
    if (score >= 26) return 'maturity-26-50';
    return 'maturity-1-25';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Progreso Personal</h1>
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value={3}>Últimos 3 meses</option>
            <option value={6}>Últimos 6 meses</option>
            <option value={12}>Últimos 12 meses</option>
          </select>
          <button
            onClick={() => navigate('/comparison')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center"
          >
            Comparar Sesiones
            <ArrowRightIcon className="h-4 w-4 ml-2" />
          </button>
        </div>
      </div>

      {/* Progress Chart */}
      <div className="bg-white rounded-lg card-shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Evolución Temporal</h2>
        <div className="chart-container">
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>

      {/* AI Assistant Section for Progress */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-6 card-shadow">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-indigo-900 flex items-center">
            <SparklesIcon className="h-6 w-6 text-indigo-600 mr-2" />
            Análisis de Tendencia Histórica (IA)
          </h2>
          {!aiInsight && !loadingAi && (
            <button
              onClick={handleGenerateInsight}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center text-sm shadow-sm hover:shadow-md"
            >
              <SparklesIcon className="h-4 w-4 mr-2" />
              Evaluar mi Progreso
            </button>
          )}
        </div>
        
        {loadingAi ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mb-4"></div>
            <p className="text-indigo-600 animate-pulse">La Inteligencia Artificial está analizando tu historial...</p>
          </div>
        ) : aiInsight ? (
          <div className="prose prose-indigo max-w-none text-gray-800 bg-white bg-opacity-60 p-5 rounded-lg">
            <ReactMarkdown>{aiInsight}</ReactMarkdown>
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            <p>Presiona el botón para que nuestra Inteligencia Artificial evalúe tu tendencia de crecimiento a lo largo del tiempo.</p>
          </div>
        )}
      </div>

      {/* Maturity Distribution */}
      <div className="bg-white rounded-lg card-shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Distribución de Niveles de Madurez</h2>

        {Object.keys(maturityDistribution).length === 0 ? (
          <p className="text-gray-500 text-center py-8">No hay datos de madurez disponibles</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {Object.entries(maturityDistribution).map(([pillar, levels]) => (
              <div key={pillar} className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">{pillar}</h3>
                <div className="space-y-2">
                  {levels.map((level, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-2 ${level.level === 'Optimizado / Mentor' ? 'bg-green-500' :
                          level.level === 'Competente / Consistente' ? 'bg-blue-500' :
                            level.level === 'En Desarrollo' ? 'bg-yellow-500' :
                              'bg-red-500'
                          }`}></div>
                        <span className="text-sm text-gray-700">{level.level}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {/* Pluralizar correctamente: 1 sesión / N sesiones */}
                        <span className="text-sm text-gray-600">{level.count} {level.count === 1 ? 'sesión' : 'sesiones'}</span>
                        <span className="text-sm font-medium text-gray-900">{level.average_score}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Matriz DOFA Personal Asistida por IA */}
      <div className="bg-white rounded-lg p-6 card-shadow border border-gray-100">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <SparklesIcon className="h-7 w-7 text-indigo-600 mr-2" />
              Matriz DOFA Personal
            </h2>
            <p className="text-gray-500 mt-1">Análisis profundo generado por IA basado en tus últimas pruebas.</p>
          </div>
          {!dofaInsight && !loadingDofa && recentSessions.length > 0 && (
            <button
              onClick={handleGenerateDofa}
              className="mt-4 sm:mt-0 bg-indigo-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center shadow-md"
            >
              <SparklesIcon className="h-5 w-5 mr-2" />
              Generar Matriz DOFA
            </button>
          )}
        </div>

        {loadingDofa ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
            <p className="text-indigo-600 text-lg animate-pulse font-medium">Diseñando tu Matriz DOFA con Inteligencia Artificial...</p>
          </div>
        ) : dofaInsight ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            {/* Fortalezas (Verde) */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 shadow-sm transform transition hover:-translate-y-1 hover:shadow-md">
              <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center border-b border-green-200 pb-2">
                <ArrowTrendingUpIcon className="h-6 w-6 mr-2" /> Fortalezas (Internas)
              </h3>
              <ul className="space-y-3">
                {dofaInsight.fortalezas?.map((item, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-green-500 mr-2 font-bold">•</span>
                    <span className="text-green-900">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Oportunidades (Azul) */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 shadow-sm transform transition hover:-translate-y-1 hover:shadow-md">
              <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center border-b border-blue-200 pb-2">
                <SparklesIcon className="h-6 w-6 mr-2" /> Oportunidades (Externas)
              </h3>
              <ul className="space-y-3">
                {dofaInsight.oportunidades?.map((item, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-blue-500 mr-2 font-bold">•</span>
                    <span className="text-blue-900">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Debilidades (Naranja) */}
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-5 shadow-sm transform transition hover:-translate-y-1 hover:shadow-md">
              <h3 className="text-xl font-bold text-orange-800 mb-4 flex items-center border-b border-orange-200 pb-2">
                <ChartBarIcon className="h-6 w-6 mr-2" /> Debilidades (Internas)
              </h3>
              <ul className="space-y-3">
                {dofaInsight.debilidades?.map((item, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-orange-500 mr-2 font-bold">•</span>
                    <span className="text-orange-900">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Amenazas (Rojo) */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 shadow-sm transform transition hover:-translate-y-1 hover:shadow-md">
              <h3 className="text-xl font-bold text-red-800 mb-4 flex items-center border-b border-red-200 pb-2">
                <svg className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Amenazas (Externas)
              </h3>
              <ul className="space-y-3">
                {dofaInsight.amenazas?.map((item, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-red-500 mr-2 font-bold">•</span>
                    <span className="text-red-900">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="text-center py-10 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
            <ChartBarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p>Genera tu matriz para descubrir tus fortalezas, oportunidades, debilidades y amenazas.</p>
          </div>
        )}
      </div>

      {/* Detailed Maturity Scores */}
      {progressData?.maturity_scores && (
        <div className="bg-white rounded-lg card-shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Detalles de Madurez</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pilar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Porcentaje
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nivel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Evidencia
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {progressData.maturity_scores.map((score, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {score.pillar_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(score.session_date).toLocaleDateString('es-ES')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`flex-1 bg-gray-200 rounded-full h-2 mr-3 ${getMaturityLevelColor(score.percentage_score)}`}>
                          <div
                            className="bg-indigo-600 h-2 rounded-full progress-bar"
                            style={{ width: `${score.percentage_score}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {score.percentage_score}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {score.maturity_level}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {score.evidence_notes}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Progress;
